import React, { useState, useRef, useEffect } from 'react';
import ExportConfigModal from './ExportConfigModal.tsx'; // Import the new modal

interface ExportReportModalProps {
  onClose: () => void;
  reportTitle?: string; // Add optional reportTitle prop
}

// Simple SVG Icons (can be replaced with more elaborate ones or an icon library)
const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
  </svg>
);

const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" style={{ width: '16px', height: '16px' }}>
        <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.857-9.809a.75.75 0 0 0-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 1 0-1.06 1.061l2.5 2.5a.75.75 0 0 0 1.137-.089l4-5.5Z" clipRule="evenodd" />
    </svg>
);

const FileDownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '32px', height: '32px' }}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
    </svg>
);


type ExportFormat = 'CSV' | 'Excel' | 'PDF';
type ModalView = 'selectFormat' | 'configureExport'; // Define view states

const ExportReportModal: React.FC<ExportReportModalProps> = ({ onClose, reportTitle }) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('CSV');
  const [view, setView] = useState<ModalView>('selectFormat'); // Add state for current view
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the modal - only if format selection is active
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (view === 'selectFormat' && modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, view]); // Add view to dependency array

  const handleContinue = () => {
    // Instead of closing, switch to the configuration view
    setView('configureExport');
    // console.log('Exporting report as:', selectedFormat);
    // onClose(); // Don't close modal here anymore
  };

  const handleBack = () => {
      setView('selectFormat'); // Go back to format selection
  };

  const formats: { name: ExportFormat; description: string; extension: string }[] = [
    { name: 'CSV', description: 'Simple text format compatible with all spreadsheet apps', extension: '.csv' },
    { name: 'Excel', description: 'Microsoft Excel format with formatting preserved', extension: '.xlsx' },
    { name: 'PDF', description: 'Portable document format for viewing and printing', extension: '.pdf' },
  ];

  const cardBaseStyle: React.CSSProperties = {
      border: '1px solid #e5e7eb', // Light gray border
      borderRadius: '8px',
      padding: '20px', // Reduced padding
      textAlign: 'center',
      cursor: 'pointer',
      position: 'relative',
      transition: 'border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      backgroundColor: 'white',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px', // Slightly reduced gap inside card
      width: '180px', // Reduced fixed width slightly
      height: '210px', // Set a fixed height to ensure uniformity
  };

  const cardSelectedStyle: React.CSSProperties = {
      borderColor: '#1f2937', // Darker border when selected
      boxShadow: '0 0 0 1px #1f2937', // Ring effect
      justifyContent: 'center',
  };

  const checkmarkStyle: React.CSSProperties = {
      position: 'absolute',
      top: '12px',
      right: '12px',
      backgroundColor: '#1f2937', // Dark background for checkmark circle
      color: 'white',
      borderRadius: '50%',
      width: '20px',
      height: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
  };

  const iconContainerStyle: React.CSSProperties = {
      backgroundColor: '#f3f4f6', // Light gray background for icon
      borderRadius: '50%',
      width: '56px',
      height: '56px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#6b7280', // Icon color
      marginTop: '8px' // Add some top margin to icon
  };

  const extensionBadgeStyle: React.CSSProperties = {
      backgroundColor: '#f3f4f6',
      color: '#6b7280',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 500,
      display: 'inline-block',
      marginBottom: '4px' // Add some bottom margin
  };


  return (
    <> { /* Use Fragment because we conditionally render different modals */}
        {view === 'selectFormat' && (
             // Backdrop for Format Selection
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)', // Darkened background
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000, // Ensure modal is on top
              padding: '20px' // Add padding for smaller screens
            }}>
              {/* Modal Content */}
              <div
                ref={modalRef}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  width: '100%',
                  maxWidth: '700px', // Max width from screenshot
                  overflow: 'hidden', // Clip content
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {/* Modal Header */}
                <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Export {reportTitle || 'Custom Report'}</h2>
                    <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>Configure your export options to download the report data</p>
                  </div>
                  <button
                    onClick={onClose}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      color: '#6b7280',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <CloseIcon />
                  </button>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '32px 24px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '24px', fontSize: '16px', fontWeight: 500, color: '#111827' }}>Select Export Format</h3>
                  <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                    {formats.map((format) => (
                      <div
                        key={format.name}
                        style={ selectedFormat === format.name ? {...cardBaseStyle, ...cardSelectedStyle} : cardBaseStyle }
                        onClick={() => setSelectedFormat(format.name)}
                        onMouseEnter={(e) => { if (selectedFormat !== format.name) e.currentTarget.style.borderColor = '#d1d5db'; }} // Slightly darken border on hover if not selected
                        onMouseLeave={(e) => { if (selectedFormat !== format.name) e.currentTarget.style.borderColor = '#e5e7eb'; }} // Restore border
                      >
                        {selectedFormat === format.name && (
                          <div style={checkmarkStyle}>
                            <CheckIcon />
                          </div>
                        )}
                         <div style={iconContainerStyle}>
                            <FileDownloadIcon />
                         </div>
                         <div style={{ fontWeight: 600, fontSize: '16px', color: '#111827', marginTop: '4px' }}>{format.name}</div>
                         <p style={{ fontSize: '13px', color: '#6b7280', margin: '0', flexGrow: 1, lineHeight: '1.4' /* Improve text readability */ }}>{format.description}</p>
                         <span style={extensionBadgeStyle}>{format.extension}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Modal Footer */}
                <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleContinue}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: '#1f2937', // Dark background (match dashboard primary)
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease-in-out',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'} // Slightly lighter on hover
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                  >
                    Continue
                  </button>
                </div>
              </div>
            </div>
          )}

        {view === 'configureExport' && (
            <ExportConfigModal
                selectedFormat={selectedFormat}
                onClose={onClose}
                onBack={handleBack}
                reportTitle={reportTitle} // Pass title along
            />
        )}
     </>
  );
};

export default ExportReportModal; 